/* ============================================================
   HALAMAN PRODUK MARKETPLACE  (/marketplace/p/:id)  —  v3.9
   ============================================================
   Pasangan rupa v3.9 bagi MarketplaceProductView.jsx. Route memilih
   antara kedua-duanya ikut suis `config/ui_v39` (lihat App.jsx).
   Halaman lama TIDAK dipadam — itu yang menjadikan patah balik
   percuma dan serta-merta.

   ⛔ LOGIK DIPINDAH BULAT-BULAT, BUKAN DITULIS SEMULA.
      `handleFree` dan `handleBuy` menyentuh duit dan hak akses
      pelanggan: tempahan dibuat, bukti bayar dimuat naik, ebook
      dilepaskan. Urutan panggilan, semakan log masuk, mesej dan
      destinasi selepas berjaya semuanya kekal sama seperti halaman
      lama. Yang berubah hanya rupa.

   ⚠️ Halaman ini TIADA versi prototaip -v3.9. Ia hidup sahaja —
      jadi tiada `modSebenar` di sini, tidak seperti
      MarketplaceV39View yang berkhidmat kepada dua laluan.

   ⚠️ Harga daripada Firestore dalam SEN. Guna `formatPrice`.
   ============================================================ */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Ban, Download, Headset, KeyRound, Receipt, ShieldCheck, ShoppingBasket, ShoppingCart } from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, Card, EmptyState, SuisBahasa } from '../design/ui';
import { IKON_KATEGORI, IKON_LAIN } from './marketplaceIcons';
import { auth } from '../firebase';
import {
    getProduct, formatPrice, categoryLabel,
    createMarketplaceBill, claimFreeEbook, getMarketplaceDownloadUrl,
} from '../services/marketplaceService';
import { toast } from './toastBus';
import { useT } from '../i18n';
import { tambahKeTroli, HAD_TROLI } from '../services/cartStore';
import { Kuantiti, ButangTroli } from './TroliV39View';

/* Label kategori ikut bahasa — kunci SAMA dengan penapis kategori di
   halaman grid Marketplace. `categoryLabel` (servis) hanya ada versi
   Melayu; ia kekal sebagai sandaran untuk kategori baharu yang belum
   masuk kamus. */
const KUNCI_KATEGORI = {
    ebook: 'products.market.cat.ebook',
    tiktok_account: 'products.market.cat.tiktok',
    flow_ultra: 'products.market.cat.flowUltra',
};

/* Pil kecil kelabu — kategori dan stok. Bukan Badge kerana Badge
   membawa nada status (ok/warn/stop); dua ini cuma maklumat. */
function Pil({ ikon, children }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'var(--s-fill)', border: '1px solid var(--s-line)',
            color: 'var(--s-ink-2)', fontSize: 12, padding: '4px 10px', borderRadius: 999,
        }}>
            {ikon}{children}
        </span>
    );
}

export default function MarketplaceProductV39View() {
    const { productId } = useParams();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showBuy, setShowBuy] = useState(false);
    const [whatsapp, setWhatsapp] = useState('');
    const [kuantiti, setKuantiti] = useState(1);
    const t = useT();

    useEffect(() => {
        let hidup = true;
        (async () => {
            try {
                const p = await getProduct(productId);
                if (hidup) setProduct(p);
            } catch (e) {
                toast.error(t('market.detail.loadFailed', { ralat: e?.message || e }));
            } finally {
                if (hidup) setLoading(false);
            }
        })();
        return () => { hidup = false; };
        /* `t` sengaja TIDAK dimasukkan — ia bertukar bila bahasa bertukar,
           dan menambahnya akan membaca produk dari Firestore sekali lagi
           (corak sama MarketplaceV39View). */
    }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Tindakan (dipindah bulat-bulat daripada halaman lama) ───────── */

    async function handleFree() {
        if (!auth.currentUser) { toast.error(t('market.cart.loginFirst')); return; }
        setBusy(true);
        try {
            const { orderId } = await claimFreeEbook(product.id);
            try {
                const { url } = await getMarketplaceDownloadUrl(orderId);
                window.open(url, '_blank');
            } catch { /* muat turun boleh dicuba semula dari Pembelian Saya */ }
            toast.success(t('market.detail.freeClaimed'));
            navigate('/marketplace/pembelian');
        } catch (e) {
            toast.error(t('market.detail.failed', { ralat: e?.message || e }));
        } finally {
            setBusy(false);
        }
    }

    /* Bayaran layan diri. Server yang mencipta pesanan + bil (harga dibaca di
       sana, bukan dihantar dari sini), kemudian pelanggan dipindahkan ke
       Billplz. Webhook yang melepaskan akses selepas bayaran sah — tiada
       langkah admin di antaranya.
       ⚠️ `setBusy(false)` sengaja TIDAK dipanggil pada laluan berjaya: halaman
          sedang berpindah, dan butang yang hidup semula mengundang klik kedua. */
    async function handleBuy() {
        if (!auth.currentUser) { toast.error(t('market.cart.loginFirst')); return; }
        setBusy(true);
        try {
            const { url } = await createMarketplaceBill(product, { whatsapp: whatsapp.trim(), qty: kuantiti });
            window.location.href = url;
        } catch (e) {
            toast.error(t('market.cart.payFailed', { ralat: e?.message || e }));
            setBusy(false);
        }
    }

    /* ── Rupa ────────────────────────────────────────────────────────── */

    const kembali = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <Btn variant="quiet" size="sm" icon={<ArrowLeft size={15} strokeWidth={2} />}
                onClick={() => navigate('/marketplace')}>
                {t('market.cart.back')}
            </Btn>
            {/* Pil bahasa ELEMEN TERAKHIR di kanan — kedudukan sama seperti
                kepala halaman lain (halaman ini tiada PageHeader). */}
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ButangTroli onNavigate={(p) => navigate(p)} />
                <SuisBahasa />
            </span>
        </div>
    );

    if (loading) {
        return (
            <>
                <div className="sesaat-m-tight" style={S.page}>
                    {kembali}
                    <div className="mkt39-detail" style={S.layout}>
                        <div className="s-skel" style={{ aspectRatio: '1 / 1', borderRadius: 'var(--s-r-lg)' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {[22, 34, 74, 92].map((t, i) => (
                                <div key={i} className="s-skel" style={{ height: t, borderRadius: 'var(--s-r)' }} />
                            ))}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (!product) {
        return (
            <>
                <div className="sesaat-m-tight" style={S.page}>
                    {kembali}
                    <EmptyState
                        title={t('market.detail.notFoundTitle')}
                        action={<Btn onClick={() => navigate('/marketplace')}>{t('purchases.toMarketplace')}</Btn>}
                    >
                        {t('market.detail.notFoundDesc')}
                    </EmptyState>
                </div>
            </>
        );
    }

    const Ikon = IKON_KATEGORI[product.category] || IKON_LAIN;
    const isFree = product.pricingMode === 'free';
    const soldOut = product.status === 'sold_out' || product.stock === 0;
    const isManual = product.fulfillment === 'manual';
    const isAutoStock = product.fulfillment === 'auto_stock';
    const tempoh = Number(product.durationDays) || 0;
    const disc = product.originalPrice > product.price && !isFree
        ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;

    /* Produk SEWA sentiasa satu unit — membeli semula bermakna MENYAMBUNG akaun
       yang sama, jadi "2 unit" tiada makna. Pagar ini mesti kekal selari dengan
       server (createMarketplaceBill) dan enjin penghantaran. */
    const sewa = tempoh > 0;
    const maksKuantiti = sewa
        ? 1
        : Math.min(HAD_TROLI.kuantiti, typeof product.stock === 'number' ? product.stock : HAD_TROLI.kuantiti);

    function masukTroli() {
        const n = tambahKeTroli(product.id, kuantiti);
        if (n === -1) { toast.error(t('market.cart.full', { n: HAD_TROLI.jenis })); return; }
        toast.success(t('market.cart.added', { n }));
    }

    return (
        <>
            <div className="sesaat-m-tight" style={S.page}>
                {kembali}

                <div className="mkt39-detail" style={S.layout}>

                    {/* ─── Gambar ─── */}
                    <div style={{
                        aspectRatio: '1 / 1', borderRadius: 'var(--s-r-lg)', overflow: 'hidden',
                        border: '1px solid var(--s-line)', position: 'relative',
                        display: 'grid', placeItems: 'center',
                        background: product.imageUrl
                            ? `var(--s-card) center/cover no-repeat url(${JSON.stringify(product.imageUrl)})`
                            : 'var(--s-card)',
                    }}>
                        {!product.imageUrl && <Ikon size={54} strokeWidth={1.3} style={{ color: 'var(--s-ink-3)' }} />}
                        {soldOut && (
                            <div style={{
                                position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
                                background: 'rgba(17,17,17,.62)',
                            }}>
                                <span style={{
                                    fontSize: 13, fontWeight: 600, letterSpacing: '.08em', color: 'var(--s-ink)',
                                    border: '1px solid var(--s-line-strong)', padding: '7px 20px', borderRadius: 999,
                                }}>{t('products.market.soldOut')}</span>
                            </div>
                        )}
                    </div>

                    {/* ─── Maklumat ─── */}
                    <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Pil ikon={<Ikon size={12} strokeWidth={2} />}>
                                {KUNCI_KATEGORI[product.category] ? t(KUNCI_KATEGORI[product.category]) : categoryLabel(product.category)}
                            </Pil>
                            {typeof product.stock === 'number' && !soldOut && <Pil>{t('market.detail.stock', { n: product.stock })}</Pil>}
                        </div>

                        <h1 style={{
                            fontSize: 24, fontWeight: 600, color: 'var(--s-ink)',
                            margin: '14px 0 16px', lineHeight: 1.3, letterSpacing: '-.01em',
                        }}>
                            {product.title}
                        </h1>

                        {/* Harga */}
                        <Card style={{ padding: '14px 18px', marginBottom: 12 }}>
                            {isFree ? (
                                <span style={{ fontSize: 22, fontWeight: 600, color: 'var(--s-ok)' }}>{t('products.market.free')}</span>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{
                                        fontSize: 26, fontWeight: 600, color: 'var(--s-ink)',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}>
                                        {formatPrice(product.price)}
                                    </span>
                                    {disc > 0 && (
                                        <>
                                            <span className="s-dim" style={{ fontSize: 15, textDecoration: 'line-through' }}>
                                                {formatPrice(product.originalPrice)}
                                            </span>
                                            <Badge tone="stop">{t('market.detail.save', { n: disc })}</Badge>
                                        </>
                                    )}
                                </div>
                            )}
                        </Card>

                        {/* Cara barang sampai */}
                        <Card style={{ padding: '13px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                            <span style={{ color: 'var(--s-ink-2)', flexShrink: 0, marginTop: 1 }}>
                                {isManual ? <Headset size={18} strokeWidth={1.8} />
                                    : isAutoStock ? <KeyRound size={18} strokeWidth={1.8} />
                                        : <Download size={18} strokeWidth={1.8} />}
                            </span>
                            <div>
                                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--s-ink)' }}>
                                    {isManual ? t('market.detail.fulfil.manual')
                                        : isAutoStock ? t('market.detail.fulfil.autoStock')
                                            : t('market.detail.fulfil.autoDownload')}
                                </div>
                                <div className="s-hint" style={{ marginTop: 3 }}>
                                    {isManual
                                        ? t('market.detail.fulfil.manualDesc')
                                        : isAutoStock
                                            ? (tempoh > 0
                                                ? t('market.detail.fulfil.autoStockDaysDesc', { n: tempoh })
                                                : t('market.detail.fulfil.autoStockDesc'))
                                            : isFree
                                                ? t('market.detail.fulfil.freeDesc')
                                                : t('market.detail.fulfil.autoDownloadDesc')}
                                </div>
                            </div>
                        </Card>

                        {product.description && (
                            <p style={{
                                color: 'var(--s-ink-2)', fontSize: 14, lineHeight: 1.75,
                                whiteSpace: 'pre-wrap', margin: '0 0 8px',
                            }}>
                                {product.description}
                            </p>
                        )}

                        {/* ─── Tindakan ─── */}
                        {soldOut ? (
                            <Btn disabled icon={<Ban size={17} strokeWidth={2} />} style={{ marginTop: 14 }}>
                                {t('market.detail.soldOutBtn')}
                            </Btn>
                        ) : isFree ? (
                            <Btn disabled={busy} onClick={handleFree}
                                icon={<Download size={17} strokeWidth={2} />} style={{ marginTop: 14 }}>
                                {busy ? t('market.detail.processing') : t('market.detail.getFree')}
                            </Btn>
                        ) : !showBuy ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                                {!sewa && (
                                    <>
                                        <span className="s-hint">{t('market.cart.qtyLabel')}</span>
                                        <Kuantiti nilai={kuantiti} maks={maksKuantiti} kunci={false} t={t}
                                            onUbah={setKuantiti} />
                                    </>
                                )}
                                <Btn onClick={() => setShowBuy(true)}
                                    icon={<ShoppingCart size={17} strokeWidth={2} />}>
                                    {t('market.detail.buy', { harga: formatPrice(product.price * kuantiti) })}
                                </Btn>
                                <Btn variant="ghost" onClick={masukTroli}
                                    icon={<ShoppingBasket size={17} strokeWidth={2} />}>
                                    {t('market.cart.addToCart')}
                                </Btn>
                            </div>
                        ) : (
                            <Card style={{ marginTop: 16, padding: 18 }}>
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 7,
                                    fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', marginBottom: 6,
                                }}>
                                    <Receipt size={16} strokeWidth={2} style={{ color: 'var(--s-ink-2)' }} />
                                    {t('market.detail.checkoutTitle')}
                                </div>
                                {/* Satu ayat penuh setiap keadaan (peraturan kamus: jangan
                                    cantum serpihan) — "Pembelian Saya" tidak lagi ditebalkan. */}
                                <p className="s-hint" style={{ margin: '0 0 14px' }}>
                                    {isAutoStock ? t('market.detail.payNoteAutoStock') : t('market.detail.payNote')}
                                </p>

                                <div style={{ marginBottom: 16 }}>
                                    <div className="s-lbl">{t('market.cart.whatsapp')}</div>
                                    <input
                                        className="s-input"
                                        value={whatsapp}
                                        onChange={(e) => setWhatsapp(e.target.value)}
                                        placeholder={t('market.cart.whatsappPh')}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Btn disabled={busy} onClick={handleBuy}
                                        icon={<ShieldCheck size={16} strokeWidth={2} />}>
                                        {busy ? t('market.cart.preparing') : t('market.cart.checkout', { jumlah: formatPrice(product.price * kuantiti) })}
                                    </Btn>
                                    <Btn variant="ghost" disabled={busy} onClick={() => setShowBuy(false)}>{t('common.cancel')}</Btn>
                                </div>
                            </Card>
                        )}
                    </div>
                </div>
            </div>

            {/* Halaman lama mentakrif media query yang sama tetapi TERLUPA
                memasang kelasnya pada susun atur — jadi pada telefon ia
                kekal dua lajur dan tersepit. Di sini kelas itu dipasang. */}
            <style>{`
                @media (max-width: 860px) {
                    .mkt39-detail { grid-template-columns: 1fr !important; }
                }
            `}</style>
        </>
    );
}

const S = {
    /* ⚠️ DIHADKAN LEBARNYA, dan itu SENGAJA — bukan terlepas pandang.
       Halaman grid dan senarai v3.9 memang penuh lebar (Products, Sales,
       Marketplace). Halaman BACAAN pula dihadkan: Changelog dan Help
       kedua-duanya 780px. Halaman ini condong ke arah bacaan — ia ada
       perenggan penerangan dan borang pesanan. Dibiar penuh lebar pada
       skrin besar, ayat penerangan menjangkau ~150 aksara sebaris dan
       medan No. WhatsApp merentang hampir seluruh skrin.
       Nak tukar ke penuh lebar? Buang dua nilai di bawah — tetapi
       hadkan perenggan penerangan dan panel pesanan secara berasingan,
       kalau tidak halaman ini jadi susah dibaca dan janggal diisi. */
    page: { padding: '26px 28px 48px', maxWidth: 1020, margin: '0 auto' },
    layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 420px) 1fr', gap: 28, alignItems: 'start' },
};
